// Espelho da regra do backend (backend/src/helpers/CanAccessTicket.ts), que é a
// fonte da verdade e responde 403 em /tickets/:id. Aqui ela serve só para a lista
// em tempo real: se divergir do backend, ou some ticket que o usuário pode abrir,
// ou aparece ticket "fantasma" que dá "Acesso não permitido" ao clicar.
//
// Semântica do escopo por conexão (`ticketConnectionScope=enabled`): a conexão
// gerenciada é um PRÉ-FILTRO estrito — ticket de conexão fora das gerenciadas é
// inacessível mesmo com allTicket ou fila, mas a conexão nunca LIBERA acesso
// sozinha. As portas continuam as mesmas: ticket com fila exige a fila; ticket
// sem fila exige allTicket (mesmo gerenciando a conexão).

export const isAdminUser = (user) =>
  user?.profile === "admin" || user?.super === true;

// Ticket inativo = ticket de conexão excluída (Tickets.active = 0). Só leitura e
// só para admin/super — mesma regra do backend (helpers/InactiveTicketAccess.ts).
export const isInactiveTicket = (ticket) => ticket?.active === false;

// "O ticket inativo pode aparecer na lista dele?" — só admin/super vê.
export const canSeeTicketInList = (user, ticket) =>
  !isInactiveTicket(ticket) || isAdminUser(user);

// Conexões que o usuário controla: a padrão (Users.whatsappId) ∪ as atribuídas
// (N:N UserWhatsapps, serializadas em `user.whatsapps`).
//
// Retorna `null` quando os dados ainda não estão carregados (sessão antiga ou
// objeto `user` parcial, sem o array `whatsapps`). Quem chama NÃO deve bloquear
// nem remover tickets com base em `null` — o backend é a autoridade e a lista
// inicial veio dele. `[]` significa "carregado e sem nenhuma conexão".
export const getManagedWhatsappIds = (user) => {
  if (!user || user.whatsapps === undefined) return null;

  const ids = [];
  if (user?.whatsappId) ids.push(Number(user.whatsappId));
  if (Array.isArray(user?.whatsapps)) {
    user.whatsapps.forEach((w) => {
      const id = Number(w?.id ?? w);
      if (Number.isFinite(id)) ids.push(id);
    });
  }
  return Array.from(new Set(ids.filter((id) => Number.isFinite(id))));
};

// Só as conexões que o usuário controla, independente do escopo por conexão
// (filtro de conexões em /tickets). Admin vê todas. Com `user` ainda não
// carregado (`null`), devolve vazio para não exibir conexão alheia.
export const managedWhatsappsForUser = (user, whatsapps) => {
  const list = Array.isArray(whatsapps) ? whatsapps : [];
  if (isAdminUser(user)) return list;
  const managed = getManagedWhatsappIds(user);
  if (managed === null) return [];
  return list.filter((w) => managed.includes(Number(w.id)));
};

// Conexões que o usuário pode VER em seletores (página de Contatos, novo
// chamado, aba de contato). Com o escopo por conexão habilitado, não-admin
// enxerga só as conexões que controla; sem nenhuma atribuída, não enxerga
// nenhuma (mesma regra do ListTicketsService).
export const visibleWhatsappsForUser = (user, whatsapps, connectionScopeEnabled) => {
  const list = Array.isArray(whatsapps) ? whatsapps : [];
  if (!connectionScopeEnabled) return list;
  return managedWhatsappsForUser(user, list);
};

const ticketQueueId = (ticket) => {
  if (ticket?.queueId != null) return Number(ticket.queueId);
  if (ticket?.queue?.id != null) return Number(ticket.queue.id);
  return null;
};

const ticketWhatsappId = (ticket) =>
  ticket?.whatsappId ? Number(ticket.whatsappId) : null;

const userHasQueue = (user, queueId) =>
  (user?.queues || []).some((queue) => Number(queue.id) === queueId);

// "O usuário pode ABRIR este ticket?"
export const canAccessTicket = (user, ticket, connectionScopeEnabled) => {
  if (!user || !ticket) return false;
  if (isAdminUser(user)) return true;
  if (isInactiveTicket(ticket)) return false;

  if (connectionScopeEnabled) {
    const managed = getManagedWhatsappIds(user);
    const whatsappId = ticketWhatsappId(ticket);

    // Conexão fora das gerenciadas => nega (allTicket/fila não furam o escopo).
    // managed === null (dados não carregados) não bloqueia — backend decide.
    if (managed !== null && whatsappId != null && !managed.includes(whatsappId)) {
      return false;
    }
  }

  // Portas de entrada (com ou sem escopo): fila do ticket, ou allTicket para
  // tickets sem fila — gerenciar a conexão NÃO libera sozinho.
  const queueId = ticketQueueId(ticket);
  if (queueId != null) return userHasQueue(user, queueId);
  return user.allTicket === "enabled";
};

// "O ticket entra na LISTA dele?" — espelha o escopo por conexão do
// ListTicketsService, que limita whatsappId às conexões gerenciadas para
// qualquer ticket, tenha fila ou não. Sem conexão nenhuma => não vê nada.
export const isWithinConnectionScope = (user, ticket, connectionScopeEnabled) => {
  if (!connectionScopeEnabled || isAdminUser(user)) return true;

  const managed = getManagedWhatsappIds(user);
  // Dados não carregados: não remover da lista — o backend já filtrou.
  if (managed === null) return true;
  if (managed.length === 0) return false;

  const whatsappId = ticketWhatsappId(ticket);
  return whatsappId != null && managed.includes(whatsappId);
};

// Quem é avisado de um ticket — som de mensagem nova (NotificationsPopOver) e
// ligação recebida (CallContext). Chamado com atendente: só o computador do
// atendente. Aguardando: quem pode abrir o chamado.
//
// Grupo fica de fora, a não ser que a empresa ligue `notifyGroupMessages` (só o
// NotificationsPopOver passa `notifyGroups`; ligação de grupo nunca avisa). Aí o
// grupo segue a mesma regra do chamado individual.
export const isTicketInAlertScope = (
  user,
  ticket,
  connectionScopeEnabled,
  notifyGroups = false
) => {
  if (!ticket) return false;
  if (ticket.isGroup && !notifyGroups) return false;
  if (ticket.userId) return Number(ticket.userId) === Number(user?.id);
  return canAccessTicket(user, ticket, connectionScopeEnabled);
};

// Admin abre qualquer chamado, mas o aviso do Aguardando é só das filas em que
// ele está cadastrado (sem fila continua avisando).
export const shouldAlertForTicket = (
  user,
  ticket,
  connectionScopeEnabled,
  notifyGroups = false
) => {
  if (!isTicketInAlertScope(user, ticket, connectionScopeEnabled, notifyGroups)) return false;
  if (ticket.userId || !isAdminUser(user)) return true;

  const queueId = ticketQueueId(ticket);
  if (queueId == null) return true;
  return userHasQueue(user, queueId);
};
